import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

const BG = "#F8F4EE";
const CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const GREEN = "#5C8A5C";
const RED = "#B85C3A";
const ORANGE = "#D68910";

export default function InvestorReportPage({ cabinetId = null }) {
  const [projects, setProjects] = useState([]);
  const [financeEntries, setFinanceEntries] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  const cabinetName =
    typeof window !== "undefined"
      ? localStorage.getItem("yoma_company_name") || "MON CABINET"
      : "MON CABINET";

  useEffect(() => {
    if (cabinetId == null) return;
    loadAll();
  }, [cabinetId]);

  async function loadAll() {
    // 1. Charger les projets (projects a cabinet_id)
    const projectsRes = await supabase.from("projects").select("*").eq("cabinet_id", cabinetId);
    const projectList = projectsRes.data || [];
    setProjects(projectList);
    const ids = projectList.map((p) => p.id);

    // 2. milestones a cabinet_id ; finance et payment_schedule n'ont PAS cabinet_id
    const [milestonesRes, financeRes, scheduleRes] = await Promise.all([
      supabase.from("milestones").select("*").eq("cabinet_id", cabinetId),
      ids.length > 0
        ? supabase.from("finance").select("*").in("project_id", ids)
        : Promise.resolve({ data: [] }),
      ids.length > 0
        ? supabase.from("payment_schedule").select("*").in("project_id", ids)
        : Promise.resolve({ data: [] }),
    ]);

    setFinanceEntries(financeRes.data || []);
    setMilestones(milestonesRes.data || []);
    setSchedule(scheduleRes.data || []);
  }

  function formatMoney(value) {
    return Number(value || 0).toLocaleString("fr-FR") + " MAD";
  }

  function getExpectedAmount(projectBudget, percentage) {
    const budget = Number(projectBudget || 0);
    const pct = Number(percentage || 0);
    return (budget * pct) / 100;
  }

  function getProjectFinance(projectId) {
    return financeEntries.filter(
      (entry) => String(entry.project_id) === String(projectId)
    );
  }

  function getProjectMilestones(projectId) {
    return milestones.filter(
      (m) => String(m.project_id) === String(projectId)
    );
  }

  function getProjectSchedule(projectId) {
    return schedule.filter(
      (row) => String(row.project_id) === String(projectId)
    );
  }

  function getEncaisseProjet(projectId) {
    return getProjectFinance(projectId)
      .filter((entry) => entry.type === "income")
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }

  function getDecaisseProjet(projectId) {
    return getProjectFinance(projectId)
      .filter((entry) => entry.type === "expense")
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }

  function getSoldeProjet(projectId) {
    return getEncaisseProjet(projectId) - getDecaisseProjet(projectId);
  }

  function getProgressProjet(projectId) {
    const list = getProjectMilestones(projectId);
    if (list.length === 0) return 0;
    const done = list.filter((m) => m.status === "done").length;
    return Math.round((done / list.length) * 100);
  }

  function getTotalTheorique(project) {
    return getProjectSchedule(project.id).reduce((sum, row) => {
      return sum + getExpectedAmount(project.budget, row.percentage);
    }, 0);
  }

  function getResteAEncaisser(project) {
    const theorique = getTotalTheorique(project);
    const encaisse = getEncaisseProjet(project.id);
    return Math.max(theorique - encaisse, 0);
  }

  function getEncaisseInstallment(projectId, installmentNumber) {
    return financeEntries
      .filter(
        (entry) =>
          String(entry.project_id) === String(projectId) &&
          entry.type === "income" &&
          Number(entry.installment_number) === Number(installmentNumber)
      )
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }

  function getProfitFinalPrevisionnel(project) {
    return getTotalTheorique(project) - getDecaisseProjet(project.id);
  }

  function getMargeFinalePrevisionnelle(project) {
    const theorique = getTotalTheorique(project);
    const profitFinal = getProfitFinalPrevisionnel(project);

    if (theorique <= 0) return 0;
    return (profitFinal / theorique) * 100;
  }

  function getRisqueTresorerie(project) {
    const solde = getSoldeProjet(project.id);
    const reste = getResteAEncaisser(project);
    const budget = Number(project.budget || 0);

    if (solde < 0 || reste > budget * 0.5) return "Critique";
    if (solde < budget * 0.1 || reste > budget * 0.25) return "Tension";
    return "Sain";
  }

  function getCommentaireSynthese(project) {
    const marge = getMargeFinalePrevisionnelle(project);
    const risque = getRisqueTresorerie(project);
    const progress = getProgressProjet(project.id);

    if (risque === "Critique" || marge < 0) {
      return "Le projet présente à ce stade un niveau de vigilance élevé. Une revue des dépenses, des encaissements et du rythme d’avancement est recommandée immédiatement.";
    }

    if (risque === "Tension" || progress < 50) {
      return "Le projet reste globalement viable, mais nécessite un suivi rapproché des flux financiers et du calendrier d’avancement.";
    }

    return "Le projet présente actuellement des indicateurs cohérents avec une trajectoire globalement maîtrisée.";
  }

  function handlePrint() {
    window.print();
  }

  const selectedProject = projects.find(
    (p) => String(p.id) === String(selectedProjectId)
  );

  const reportDate = useMemo(
    () =>
      new Date().toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    []
  );

  const nextEcheance = useMemo(() => {
    if (!selectedProject) return null;
    const rows = getProjectSchedule(selectedProject.id)
      .slice()
      .sort((a, b) => Number(a.installment_number) - Number(b.installment_number));
    for (const row of rows) {
      const attendu = getExpectedAmount(selectedProject.budget, row.percentage);
      const enc = getEncaisseInstallment(selectedProject.id, row.installment_number);
      if (enc < attendu && attendu > 0) {
        const due =
          row.due_date ||
          row.date_echeance ||
          row.date ||
          null;
        return {
          label: row.label || `V${row.installment_number}`,
          montant: attendu - enc,
          dateStr: due
            ? new Date(due).toLocaleDateString("fr-FR")
            : "À planifier",
          v: row.installment_number,
        };
      }
    }
    if (rows.length) {
      const last = rows[rows.length - 1];
      const attendu = getExpectedAmount(selectedProject.budget, last.percentage);
      const enc = getEncaisseInstallment(selectedProject.id, last.installment_number);
      const due = last.due_date || last.date_echeance || last.date || null;
      return {
        label: last.label || `V${last.installment_number}`,
        montant: Math.max(attendu - enc, 0),
        dateStr: due ? new Date(due).toLocaleDateString("fr-FR") : "—",
        v: last.installment_number,
      };
    }
    return null;
  }, [selectedProject, selectedProjectId, financeEntries, schedule]);

  const financialRows = useMemo(() => {
    if (!selectedProject) return [];
    const budget = Number(selectedProject.budget || 0);
    const theorique = getTotalTheorique(selectedProject);
    const encaisse = getEncaisseProjet(selectedProject.id);
    const decaisse = getDecaisseProjet(selectedProject.id);
    const rows = [
      {
        poste: "Budget global projet",
        prevu: budget,
        realise: decaisse,
        ecart: budget - decaisse,
      },
      {
        poste: "Encaissements (vs prévisionnel)",
        prevu: theorique,
        realise: encaisse,
        ecart: theorique - encaisse,
      },
      {
        poste: "Solde net trésorerie",
        prevu: null,
        realise: getSoldeProjet(selectedProject.id),
        ecart: null,
      },
    ];
    return rows;
  }, [selectedProject, selectedProjectId, financeEntries, schedule]);

  const milestoneTimeline = useMemo(() => {
    if (!selectedProject) return [];
    const ms = getProjectMilestones(selectedProject.id)
      .slice()
      .sort((a, b) => Number(a.number) - Number(b.number));
    const sched = getProjectSchedule(selectedProject.id);
    return ms.map((m) => {
      const row = sched.find((r) => Number(r.milestone_number) === Number(m.number));
      const montant = row
        ? getExpectedAmount(selectedProject.budget, row.percentage)
        : null;
      const dateStr =
        m.due_date ||
        m.date_prevue ||
        m.date ||
        row?.due_date ||
        row?.date_echeance ||
        null;
      return {
        id: m.id,
        title: m.title || `Jalon ${m.number}`,
        status: m.status,
        montant,
        dateStr: dateStr ? new Date(dateStr).toLocaleDateString("fr-FR") : "—",
      };
    });
  }, [selectedProject, selectedProjectId, milestones, schedule]);

  const statutLabel = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("livré") || s.includes("term") || s.includes("done")) return { label: "Terminé", tone: GREEN };
    if (s.includes("chantier") || s.includes("construction") || s.includes("actif")) return { label: "En cours", tone: GOLD };
    return { label: "Actif", tone: TAUPE };
  };

  const decisionPoints = useMemo(() => {
    if (!selectedProject) return [];
    const pts = [];
    pts.push({ text: getCommentaireSynthese(selectedProject), type: "synthèse" });
    pts.push({
      text: `Risque trésorerie : ${getRisqueTresorerie(selectedProject)} — Marge prévisionnelle : ${getMargeFinalePrevisionnelle(selectedProject).toFixed(1)} %`,
      type: "fin",
    });
    if (getResteAEncaisser(selectedProject) > 0) {
      pts.push({
        text: `Reste à encaisser : ${formatMoney(getResteAEncaisser(selectedProject))}`,
        type: "enc",
      });
    }
    if (getSoldeProjet(selectedProject.id) < 0) {
      pts.push({
        text: `Solde négatif : ${formatMoney(getSoldeProjet(selectedProject.id))} — action recommandée.`,
        type: "alert",
      });
    }
    return pts;
  }, [selectedProject, selectedProjectId, financeEntries]);

  const selectStyle = {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #E7E1D8",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    fontSize: 14,
    color: TAUPE,
    background: CARD,
    minWidth: 260,
    maxWidth: "100%",
    cursor: "pointer",
    outline: "none",
  };

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: TAUPE }}>Chargement...</div>;
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        background: BG,
        padding: "28px 24px 48px",
        fontFamily: "'DM Sans', system-ui, sans-serif",
        color: TAUPE,
        boxSizing: "border-box",
      }}
    ><style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
          @media print {
            body { background: white !important; }
            .no-print { display: none !important; }
            main { padding: 12px !important; background: white !important; }
          }
          @media (max-width: 900px) {
            .inv-kpi { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
            .inv-timeline { overflow-x: auto; padding-bottom: 8px; }
          }
          @media (max-width: 520px) {
            .inv-kpi { grid-template-columns: 1fr !important; }
          }
        `}
      </style><header
        style={{
          maxWidth: 1100,
          marginLeft: "auto",
          marginRight: "auto",
          marginBottom: 24,
        }}
      ><div style={{ marginBottom: 16 }}><div style={{ fontSize: 12, color: "#8A7B6D", marginBottom: 6 }}>{cabinetName}</div><h1
            style={{
              margin: 0,
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 32,
              fontWeight: 600,
              color: TAUPE,
              lineHeight: 1.2,
            }}
          >
            Rapport Investisseur / Client
          </h1><p style={{ margin: "8px 0 0", fontSize: 15, color: "#6F6256" }}>Version synthétique et présentable</p></div><div
          className="no-print"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        ><select
            style={selectStyle}
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          ><option value="">Sélectionner un projet</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          {selectedProject && (
            <button
              type="button"
              onClick={handlePrint}
              style={{
                padding: "10px 22px",
                borderRadius: 10,
                border: "none",
                background: GOLD,
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: "0 2px 8px rgba(192,154,91,0.35)",
              }}
            >
              Imprimer / PDF
            </button>
          )}
        </div></header>

      {!selectedProject && (
        <div
          className="no-print"
          style={{
            maxWidth: 560,
            margin: "48px auto",
            textAlign: "center",
            padding: 32,
            background: CARD,
            borderRadius: 14,
            border: "1px solid #E7E1D8",
          }}
        ><p style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: 20 }}>Sélectionnez un projet</p><p style={{ margin: "10px 0 0", fontSize: 14, color: "#8B7361" }}>
            Choisissez un projet pour générer le rapport investisseur / client.
          </p></div>
      )}

      {selectedProject && (
        <div style={{ maxWidth: 1100, marginLeft: "auto", marginRight: "auto" }}><div
            className="inv-kpi"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 14,
              marginBottom: 22,
            }}
          ><div style={{ background: CARD, borderRadius: 14, padding: "18px 20px", border: "1px solid #E7E1D8" }}><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8B7361", marginBottom: 8 }}>
                Budget global (MAD)
              </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, color: GOLD }}>
                {formatMoney(selectedProject.budget)}
              </div></div><div style={{ background: CARD, borderRadius: 14, padding: "18px 20px", border: "1px solid #E7E1D8" }}><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8B7361", marginBottom: 8 }}>
                Avancement
              </div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: TAUPE, marginBottom: 8 }}>
                {getProgressProjet(selectedProject.id)}%
              </div><div style={{ height: 8, background: "#F0EBE3", borderRadius: 999, overflow: "hidden" }}><div
                  style={{
                    width: `${getProgressProjet(selectedProject.id)}%`,
                    height: "100%",
                    background: `linear-gradient(90deg, ${GOLD}, #E8C87A)`,
                    borderRadius: 999,
                  }}
                /></div></div><div style={{ background: CARD, borderRadius: 14, padding: "18px 20px", border: "1px solid #E7E1D8" }}><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8B7361", marginBottom: 8 }}>
                Encaissé / Total
              </div><div style={{ fontSize: 15, fontWeight: 700, color: GREEN }}>
                {formatMoney(getEncaisseProjet(selectedProject.id))}
              </div><div style={{ fontSize: 12, color: "#8B7361", marginTop: 4 }}>sur {formatMoney(getTotalTheorique(selectedProject))}</div></div><div style={{ background: CARD, borderRadius: 14, padding: "18px 20px", border: "1px solid #E7E1D8" }}><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8B7361", marginBottom: 8 }}>
                Prochaine échéance
              </div>
              {nextEcheance ? (
                <><div style={{ fontSize: 13, fontWeight: 600, color: ORANGE }}>{formatMoney(nextEcheance.montant)}</div><div style={{ fontSize: 12, color: "#8B7361", marginTop: 4 }}>{nextEcheance.label}</div><div style={{ fontSize: 12, color: TAUPE, marginTop: 2 }}>{nextEcheance.dateStr}</div></>
              ) : (
                <div style={{ fontSize: 13, color: "#9A9088" }}>—</div>
              )}
            </div></div><section
            style={{
              background: CARD,
              borderRadius: 16,
              padding: "24px 28px",
              border: "1px solid #E7E1D8",
              marginBottom: 22,
            }}
          ><h2
              style={{
                margin: "0 0 16px",
                fontFamily: "'Playfair Display', serif",
                fontSize: 22,
                fontWeight: 700,
              }}
            >
              {selectedProject.name}
            </h2><div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 14 }}><span style={{ fontSize: 14 }}><strong>Client :</strong> {selectedProject.client_name || "—"}
              </span><span style={{ color: "#8B7361" }}>·</span><span style={{ fontSize: 14, color: "#6F6256" }}>
                {selectedProject.location || selectedProject.zone || "Localisation non renseignée"}
              </span>
              {(() => {
                const st = statutLabel(selectedProject.status);
                return (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      padding: "5px 12px",
                      borderRadius: 999,
                      background: `${st.tone}18`,
                      color: st.tone,
                      border: `1px solid ${st.tone}40`,
                    }}
                  >
                    {st.label}
                  </span>
                );
              })()}
            </div><div style={{ display: "flex", flexWrap: "wrap", gap: 24, fontSize: 13, color: "#6F6256" }}><span><strong style={{ color: TAUPE }}>Début :</strong>{" "}
                {selectedProject.created_at
                  ? new Date(selectedProject.created_at).toLocaleDateString("fr-FR")
                  : "—"}
              </span><span><strong style={{ color: TAUPE }}>Fin prévisionnelle :</strong>{" "}
                {selectedProject.end_date || selectedProject.date_fin || selectedProject.deadline || selectedProject.delivery_date
                  ? new Date(
                      selectedProject.end_date ||
                        selectedProject.date_fin ||
                        selectedProject.deadline ||
                        selectedProject.delivery_date
                    ).toLocaleDateString("fr-FR")
                  : "—"}
              </span></div></section><section style={{ marginBottom: 22 }}><h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, margin: "0 0 12px" }}>Synthèse financière</h3><div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #E7E1D8", background: CARD }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}><thead><tr style={{ background: "#FCFAF7", borderBottom: "1px solid #E7E1D8" }}><th style={{ textAlign: "left", padding: "12px 16px", fontWeight: 600, color: "#8B7361" }}>Poste</th><th style={{ textAlign: "right", padding: "12px 16px", fontWeight: 600, color: "#8B7361" }}>Budget prévu</th><th style={{ textAlign: "right", padding: "12px 16px", fontWeight: 600, color: "#8B7361" }}>Réalisé</th><th style={{ textAlign: "right", padding: "12px 16px", fontWeight: 600, color: "#8B7361" }}>Écart</th></tr></thead><tbody>
                  {financialRows.map((row, i) => {
                    const ecart = row.ecart;
                    const showEcart = ecart !== null && row.prevu !== null;
                    let ecartColor = TAUPE;
                    if (showEcart) {
                      if (row.poste.includes("Budget global")) {
                        if (ecart > 0) ecartColor = GREEN;
                        else if (ecart < 0) ecartColor = RED;
                      } else if (row.poste.includes("Encaissements")) {
                        const theorique = row.prevu;
                        const enc = row.realise;
                        if (theorique > 0 && enc >= theorique) ecartColor = GREEN;
                        else if (ecart > 0) ecartColor = ORANGE;
                        else if (ecart < 0) ecartColor = RED;
                      }
                    }
                    return (
                      <tr key={row.poste} style={{ borderBottom: i < financialRows.length - 1 ? "1px solid #F0EBE3" : "none" }}><td style={{ padding: "12px 16px", fontWeight: 500 }}>{row.poste}</td><td style={{ textAlign: "right", padding: "12px 16px" }}>{row.prevu !== null ? formatMoney(row.prevu) : "—"}</td><td style={{ textAlign: "right", padding: "12px 16px" }}>{formatMoney(row.realise)}</td><td
                          style={{
                            textAlign: "right",
                            padding: "12px 16px",
                            fontWeight: 600,
                            color: ecartColor,
                          }}
                        >
                          {showEcart ? formatMoney(ecart) : "—"}
                        </td></tr>
                    );
                  })}
                </tbody><tfoot><tr style={{ background: "#F8F4EE", borderTop: `2px solid ${GOLD}` }}><td colSpan={3} style={{ padding: "14px 16px", fontWeight: 700, fontFamily: "'Playfair Display', serif", fontSize: 15 }}>
                      Total — Profit final prévisionnel
                    </td><td style={{ textAlign: "right", padding: "14px 16px", fontWeight: 700, color: GOLD, fontSize: 15 }}>
                      {formatMoney(getProfitFinalPrevisionnel(selectedProject))}
                    </td></tr></tfoot></table></div></section><section style={{ marginBottom: 22 }}><h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, margin: "0 0 14px" }}>Timeline jalons</h3>
            {milestoneTimeline.length === 0 ? (
              <p style={{ fontSize: 13, color: "#9A9088" }}>Aucun jalon enregistré pour ce projet.</p>
            ) : (
              <div
                className="inv-timeline"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0,
                  padding: "16px 8px",
                  background: CARD,
                  borderRadius: 14,
                  border: "1px solid #E7E1D8",
                  overflowX: "auto",
                }}
              >
                {milestoneTimeline.map((m, idx) => {
                  const done = m.status === "done";
                  const circleColor = done ? GREEN : "#D4C4B0";
                  return (
                    <div
                      key={m.id}
                      style={{
                        flex: "1 0 120px",
                        minWidth: 100,
                        textAlign: "center",
                        position: "relative",
                        paddingTop: 8,
                      }}
                    >
                      {idx > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            left: "-50%",
                            right: "50%",
                            top: 19,
                            height: 2,
                            background: "#E0D8CE",
                            zIndex: 0,
                          }}
                        />
                      )}
                      <div
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: circleColor,
                          margin: "0 auto 10px",
                          border: `3px solid ${CARD}`,
                          boxShadow: "0 0 0 1px #E0D8CE",
                          position: "relative",
                          zIndex: 1,
                        }}
                      /><div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3, marginBottom: 4 }}>{m.title}</div><div style={{ fontSize: 11, color: GOLD, fontWeight: 600 }}>{m.montant != null ? formatMoney(m.montant) : "—"}</div><div style={{ fontSize: 10, color: "#9A9088", marginTop: 4 }}>{m.dateStr}</div></div>
                  );
                })}
              </div>
            )}
          </section><section
            style={{
              background: CARD,
              borderRadius: 14,
              padding: "20px 24px",
              border: "1px solid #E7E1D8",
              marginBottom: 28,
            }}
          ><h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, margin: "0 0 14px" }}>Alertes &amp; décisions</h3><ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
              {decisionPoints.map((p, i) => (
                <li
                  key={i}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    borderLeft: `3px solid ${p.type === "alert" ? RED : GOLD}`,
                    background: "#FDFBF8",
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: "#4A4540",
                  }}
                >
                  {p.text}
                </li>
              ))}
            </ul></section><footer
            style={{
              textAlign: "center",
              paddingTop: 20,
              borderTop: "1px solid #E7E1D8",
              fontSize: 12,
              color: "#8B7361",
            }}
          ><div style={{ fontWeight: 600, color: TAUPE, marginBottom: 6 }}>{cabinetName}</div><div style={{ marginBottom: 6 }}>Rapport établi le {reportDate}</div><div style={{ fontSize: 11, fontStyle: "italic" }}>Document confidentiel — usage strictement privé</div></footer></div>
      )}
    </main>
  );
}
